import type { ReactNode } from 'react';
import { Expand, Loader2 } from 'lucide-react';
import type { MetricComparisonDirection, MetricPeriodComparison } from '../../lib/metricPeriodComparison';

export type MetricAccent = 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'gray';

const TREND_LINE: Record<MetricComparisonDirection, string> = {
  up: '#16a34a',
  new: '#16a34a',
  down: '#dc2626',
  flat: '#9ca3af',
};

const COMPARISON_TONE: Record<MetricComparisonDirection, string> = {
  up: 'text-emerald-600',
  down: 'text-red-600',
  flat: 'text-gray-500',
  new: 'text-emerald-600',
};

/** Normalized Y (0 = top, 1 = bottom) — jagged mini chart, left to right. */
function trendPoints(direction: MetricComparisonDirection, seed: number): number[] {
  const bump = ((seed % 4) - 1.5) * 0.04;

  if (direction === 'up' || direction === 'new') {
    return [0.9, 0.72, 0.78, 0.58, 0.66, 0.48, 0.54, 0.34, 0.4, 0.22].map((y, i) =>
      Math.min(0.94, Math.max(0.1, y + (i % 2 === 0 ? bump : -bump * 0.6)))
    );
  }

  if (direction === 'down') {
    return [0.14, 0.32, 0.26, 0.46, 0.38, 0.56, 0.5, 0.7, 0.64, 0.86].map((y, i) =>
      Math.min(0.94, Math.max(0.1, y + (i % 2 === 0 ? bump : -bump * 0.6)))
    );
  }

  return [0.52, 0.44, 0.56, 0.4, 0.54, 0.42, 0.5, 0.46, 0.54, 0.48].map((y) =>
    Math.min(0.58, Math.max(0.42, y + bump * 0.3))
  );
}

function MetricSparkline({
  direction,
  seed,
}: {
  direction: MetricComparisonDirection;
  seed: number;
}) {
  const stroke = TREND_LINE[direction];
  const w = 64;
  const h = 32;
  const points = trendPoints(direction, seed);
  const coords = points.map((y, i) => {
    const x = 2 + (i / (points.length - 1)) * (w - 4);
    const yy = y * h;
    return `${x},${yy}`;
  });
  const linePath = `M ${coords.join(' L ')}`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full h-full"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="miter"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export interface ControlCentreMetricCardProps {
  title: string;
  /** Shorter label for narrow/mobile grid cells. */
  mobileTitle?: string;
  value: string;
  /** Period-over-period compare line (e.g. "+12% from last month"). */
  comparison?: MetricPeriodComparison;
  accent?: MetricAccent;
  loading?: boolean;
  sparkSeed?: number;
  /** Shows expand control; opens detail view when clicked. */
  onExpandClick?: () => void;
  /** scroll = horizontal carousel tile; grid = full-width cell in a 2-col grid. */
  layout?: 'scroll' | 'grid';
}

const metricFont =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif";

/** Analytics tile for control-centre app widget headers. */
export function ControlCentreMetricCard({
  title,
  mobileTitle,
  value,
  comparison,
  loading = false,
  sparkSeed = 0,
  onExpandClick,
  layout = 'scroll',
}: ControlCentreMetricCardProps) {
  const hasComparison = Boolean(comparison);
  const trendDirection: MetricComparisonDirection = comparison?.direction ?? 'flat';
  const isGrid = layout === 'grid';
  const showSparkline = hasComparison && !isGrid;
  const contentPad =
    showSparkline || (onExpandClick && !isGrid) ? 'pr-[4.5rem]' : isGrid && onExpandClick ? 'pr-5' : '';
  const titlePad = onExpandClick ? (isGrid ? 'pr-5' : 'pr-6') : '';
  const layoutClass =
    layout === 'grid'
      ? 'w-full min-w-0'
      : 'flex-none snap-start w-[calc(50%-0.25rem)] min-w-[9.75rem] max-w-[11.5rem] sm:w-auto sm:min-w-[128px] sm:max-w-none sm:flex-1';

  return (
    <div
      className={`relative flex flex-col ${layoutClass} rounded-xl sm:rounded-xl border border-[#e3e5e7] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.06)] overflow-hidden ${
        isGrid ? 'px-2.5 py-2' : 'px-2.5 py-2.5 sm:py-2'
      }`}
      style={{ fontFamily: metricFont }}
    >
      {onExpandClick ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onExpandClick();
          }}
          className={`absolute z-10 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:scale-95 transition-colors touch-manipulation ${
            isGrid
              ? 'top-1 right-1 p-1 rounded-md'
              : 'top-1.5 right-1.5 p-1 rounded-md'
          }`}
          aria-label={`View ${title} details`}
        >
          <Expand
            className={isGrid ? 'h-3.5 w-3.5' : 'h-3.5 w-3.5'}
            strokeWidth={2.25}
          />
        </button>
      ) : null}
      <p
        className={`font-semibold text-gray-500 leading-tight ${contentPad} ${titlePad} ${
          isGrid
            ? 'text-[11px] line-clamp-2'
            : 'text-[9px] uppercase tracking-[0.05em] line-clamp-2'
        }`}
        style={isGrid ? { letterSpacing: '-0.005em' } : undefined}
      >
        <span className={mobileTitle ? 'hidden sm:inline' : ''}>{title}</span>
        {mobileTitle ? (
          <span className="sm:hidden">{mobileTitle}</span>
        ) : null}
      </p>
      {loading ? (
        <Loader2
          className={`animate-spin text-gray-300 ${isGrid ? 'h-4 w-4 mt-1' : 'h-5 w-5 mt-1.5'}`}
        />
      ) : (
        <p
          className={`font-bold text-gray-900 tabular-nums leading-none ${contentPad} ${
            isGrid ? 'text-[18px] mt-1' : 'text-lg mt-1 sm:text-xl'
          }`}
          style={{ letterSpacing: '-0.03em' }}
        >
          {value}
        </p>
      )}
      {comparison ? (
        <p
          className={`leading-snug font-medium ${COMPARISON_TONE[comparison.direction]} ${
            isGrid
              ? 'text-[11px] mt-1 line-clamp-1'
              : `text-[10px] mt-1.5 sm:mt-2 ${showSparkline ? 'pr-[4.5rem]' : ''}`
          }`}
        >
          {comparison.label}
        </p>
      ) : null}
      {showSparkline ? (
        <div
          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-14 h-8 pointer-events-none"
          aria-hidden
        >
          <MetricSparkline direction={trendDirection} seed={sparkSeed} />
        </div>
      ) : null}
    </div>
  );
}

/** Horizontal row of metric cards with consistent spacing. */
export function ControlCentreMetricsRow({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex gap-2 sm:gap-2.5 w-full min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-contain snap-x snap-mandatory pb-1 -mx-0.5 px-0.5 ios-momentum-scroll scrollbar-thin touch-pan-x"
      role="list"
      aria-label="Metrics"
    >
      {children}
    </div>
  );
}

/** Two-column grid for mobile widget headers (e.g. calendar reports). */
export function ControlCentreMetricsGrid({ children }: { children: ReactNode }) {
  return (
    <div
      className="grid grid-cols-2 gap-2 w-full min-w-0 sm:hidden"
      role="list"
      aria-label="Metrics"
    >
      {children}
    </div>
  );
}

